import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Combobox } from '@ibsys/kit';
import { QualityConsultantReportResponse, type HiddenCriterionRef } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { ConsultantReportBody } from './ConsultantReportBody';

type Props = {
  consultantName: string;
  consultantNames: string[];
  report: QualityConsultantReportResponse | undefined;
  onChangeConsultant: (name: string) => void;
  /** Критерии, скрытые пользователем (каждый — в разрезе своего заключения). */
  hiddenCriteria: HiddenCriterionRef[];
  /** Пользовательские пороги зон, по которым критерии раскладываются по зонным блокам. */
  zoneRanges: QualityZoneRanges;
};

/**
 * Карточка мини-отчёта по консультанту: критерии, разложенные по зонам качества,
 * сравнение с командой, динамика и таблица отклонений по критериям.
 * Отчёт отображается только после выбора консультанта в селекторе.
 */
export const ConsultantReportCard: FC<Props> = ({
  consultantName,
  consultantNames,
  report,
  onChangeConsultant,
  hiddenCriteria,
  zoneRanges,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const options = useMemo(
    () => [{ label: t('filters.all'), value: '' }, ...consultantNames.map((n) => ({ label: n, value: n }))],
    [consultantNames, t],
  );

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <CardTitle>{t('consultantReport.title')}</CardTitle>
        <Combobox
          value={consultantName}
          onChange={(v) => onChangeConsultant(String(v ?? ''))}
          options={options}
          placeholder={t('consultantReport.selectPlaceholder')}
          className="w-full sm:w-64"
        />
      </CardHeader>
      <CardContent>
        {report && report.count > 0 ? (
          <ConsultantReportBody report={report} hiddenCriteria={hiddenCriteria} zoneRanges={zoneRanges} />
        ) : (
          <div className="text-muted-foreground text-sm">{t('consultantReport.selectHint')}</div>
        )}
      </CardContent>
    </Card>
  );
};
